import BaseApi from "./BaseApi";

/**
 * API client for the Invitation / Visitor Access Control endpoints.
 * Implements the contracts defined in docs/AI_INTEGRATION_INSTRUCTIONS.md
 * sections 3.0, 3.0b, 3.1, and 3.2.
 */
class InvitationApi extends BaseApi {

    /**
     * GET /api/invitations/{guid}
     * Public — no auth required.
     * Returns invitation details and the current invitationState enum value.
     */
    async getInvitationByGuid(guid) {
        try {
            const response = await this.get(`api/invitations/${guid}`);
            return response.data;
        } catch (error) {
            const status = error.response?.status;
            if (status === 404) {
                throw new Error("INVITATION_NOT_FOUND");
            }
            throw new Error(error.response?.data?.message || error.message || "Unable to load invitation");
        }
    }

    /**
     * POST /api/invitations/{id}/request-otp
     * Triggers OTP delivery to the visitor's registered phone number.
     * Only called when OTP verification is enabled on the server.
     */
    async requestOtp(invitationGuid) {
        try {
            const response = await this.post(`api/invitations/${invitationGuid}/request-otp`, {});
            return response.data;
        } catch (error) {
            const status = error.response?.status;
            if (status === 409) {
                throw new Error("OTP_ALREADY_SENT");
            }
            if (status === 429) {
                throw new Error("OTP_RATE_LIMITED");
            }
            if (status === 404) {
                throw new Error("INVITATION_NOT_FOUND");
            }
            throw new Error(error.response?.data?.message || error.message || "Unable to send OTP");
        }
    }

    /**
     * POST /api/invitations/{id}/activate
     * Verifies OTP (when enabled) and delivers the TOTP secret seed.
     * When OTP is disabled the server accepts an empty body.
     *
     * Returns { totpSecretSeed, invitationGuid, validUntil, visitorName }
     *
     * SECURITY: The totpSecretSeed must NEVER be stored in localStorage/sessionStorage.
     * It must live only in React component state (memory).
     */
    async activateInvitation(invitationGuid, otpCode = null) {
        try {
            const body = otpCode ? { otpCode } : {};
            const response = await this.post(`api/invitations/${invitationGuid}/activate`, body);
            return response.data;
        } catch (error) {
            const status = error.response?.status;
            if (status === 401) {
                throw new Error("INVALID_OTP");
            }
            if (status === 409) {
                throw new Error("ALREADY_ACTIVATED");
            }
            if (status === 410) {
                throw new Error("INVITATION_CANCELLED");
            }
            throw new Error(error.response?.data?.message || error.message || "Activation failed");
        }
    }

    /**
     * POST /api/invitations  (homeowner authenticated)
     * Creates a new visitor invitation record. Returns the invitation URL.
     * Does NOT send any WhatsApp message — the homeowner shares it via wa.me deep link.
     */
    async createInvitation(invitationData) {
        try {
            const response = await this.post("api/invitations", invitationData);
            return response.data;
        } catch (error) {
            const status = error.response?.status;
            if (status === 400) {
                throw new Error("INVALID_INVITATION_DATA");
            }
            if (status === 403) {
                throw new Error("NOT_AUTHORIZED");
            }
            throw new Error(error.response?.data?.message || error.message || "Unable to create invitation");
        }
    }

    /**
     * DELETE /api/invitations/{guid}
     * Cancels an invitation (homeowner only).
     */
    async cancelInvitation(invitationGuid) {
        try {
            const response = await this.delete(`api/invitations/${invitationGuid}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || error.message || "Unable to cancel invitation");
        }
    }
}

export default new InvitationApi();
